import { types, Instance } from 'mobx-state-tree';
import { set } from 'mobx';
import { DefaultValues } from '../../../../utils/constants';

export const CartTotalsModel = types.model({
  qty: types.optional(types.number, 0),
  items: types.optional(types.number, 0),
  discounts: types.optional(types.number, 0),
  giftWrap: types.optional(types.number, 0),
  giftCards: types.optional(types.number, 0),
  kitItems: types.optional(types.number, 0),
  shipping: types.optional(types.number, 0),
  tax1: types.optional(types.number, 0),
  tax2: types.optional(types.number, 0),
  tax3: types.optional(types.number, 0),
  taxTotal: types.optional(types.number, 0),
  giftCardAmountUsed: types.optional(types.number, 0),
  grandTotal: types.optional(types.number, 0),
});
export interface ICartTotalsModel extends Instance<typeof CartTotalsModel> {}

export const CartMiscModel = types.model({
  comment: types.maybeNull(types.string),
  isGift: types.maybeNull(types.boolean),
});

export interface ICartMiscModel extends Instance<typeof CartMiscModel> {}

export const CartPaymentProfileCardModel = types.model({
  pCIaaSId: types.optional(types.maybeNull(types.string), null),
  cardNumber: types.maybeNull(types.string),
  last4: types.maybeNull(types.string),
  cardBrand: types.optional(types.maybeNull(types.string), null),
  cardType: types.maybeNull(types.union(types.number, types.string)),
  cardTypeName: types.maybeNull(types.string),
  cardTokenProvider: types.optional(types.maybeNull(types.string), null),
  cardToken: types.optional(types.maybeNull(types.string), null),
  holdersName: types.maybeNull(types.string),
  expMonth: types.maybeNull(types.string),
  expYear: types.maybeNull(types.string),
  cvv: types.maybeNull(types.string),
  id: types.maybeNull(types.number),
  issueNumber: types.maybeNull(types.string),
  issueMonth: types.maybeNull(types.string),
  issueYear: types.maybeNull(types.string),
  spreedlyPaymentToken: types.optional(types.maybeNull(types.string), null),
  paymentMethodFingerprint: types.optional(types.maybeNull(types.string), null),
  transactionToken: types.maybeNull(types.string),
  challengeResponse: types.maybeNull(types.string),
  browserInfo: types.maybeNull(types.string),
});

export type ICartPaymentProfileCardModel = Instance<
  typeof CartPaymentProfileCardModel
>;

export const CartPaymentProfileModel = types.model({
  id: types.maybeNull(types.number),
  method: types.maybeNull(types.string),
  card: types.optional(CartPaymentProfileCardModel, {}),
  preferred: types.maybeNull(types.boolean),
  poNumber: types.maybeNull(types.string),
});

export interface ICartPaymentProfileModel
  extends Instance<typeof CartPaymentProfileModel> {}

export const CartShippingMethodModel = types.model({
  id: types.number,
  selected: types.boolean,
  name: types.string,
  price: types.number,
  sortOrder: types.maybeNull(types.string),
  system: types.optional(types.string, DefaultValues.DEFAULT_SHIPPING_SYSTEM),
});

export interface ICartShippingMethodModel
  extends Instance<typeof CartShippingMethodModel> {}

export const CartBillingAddressModel = types
  .model({
    id: types.maybeNull(types.number),
    firstName: types.maybeNull(types.string),
    lastName: types.maybeNull(types.string),
    address1: types.maybeNull(types.string),
    address2: types.maybeNull(types.string),
    city: types.maybeNull(types.string),
    state: types.maybeNull(types.string),
    postalCode: types.maybeNull(types.string),
    country: types.maybeNull(types.string),
    companyName: types.maybeNull(types.string),
    phoneNumber: types.maybeNull(types.string),
    preferred: types.maybeNull(types.boolean),
  })
  .views(self => ({
    get hasMinimalAddress(): boolean {
      return Boolean(
        self.address1 &&
          self.city &&
          self.state &&
          self.country &&
          self.postalCode &&
          self.firstName &&
          self.lastName &&
          self.phoneNumber
      );
    },
    get hasAnyAddressData(): boolean {
      return Boolean(
        self.address1 ||
          self.city ||
          self.state ||
          self.country ||
          self.postalCode ||
          self.firstName ||
          self.lastName ||
          self.phoneNumber
      );
    },
    get hasMinimalAmazonPayAddress(): boolean {
      return Boolean(
        self.address1 &&
          self.city &&
          self.country &&
          self.postalCode &&
          self.firstName &&
          self.lastName
      );
    },
  }));

export interface ICartBillingAddressModel
  extends Instance<typeof CartBillingAddressModel> {}

export const CartShippingAddressModel = types.compose(
  CartBillingAddressModel,
  types.model({
    residential: types.maybeNull(types.boolean),
  })
);

export type ICartShippingAddressModel = Instance<
  typeof CartShippingAddressModel
>;

export const CartStripeInfoModel = types
  .model({
    clientSecretKey: types.maybeNull(types.string),
    paymentIntentId: types.maybeNull(types.string),
    paymentMethodId: types.maybeNull(types.string),
    isSavedCard: types.optional(types.boolean, false),
    cardBrand: types.maybeNull(types.string),
    last4: types.maybeNull(types.string),
    expMonth: types.optional(types.string, ''),
    expYear: types.optional(types.string, ''),
    setupIntentId: types.maybeNull(types.string),
    isWallet: types.optional(types.boolean, false),
  })
  .actions(self => {
    const setPaymentMethodId = (value: string) => {
      set(self, { paymentMethodId: value });
    };
    return { setPaymentMethodId };
  });

export interface ICartStripeInfoModel
  extends Instance<typeof CartStripeInfoModel> {}

export const CartPayPalInfoModel = types
  .model({
    paymentMethodId: types.maybeNull(types.number),
    transactionId: types.maybeNull(types.string),
    payerId: types.maybeNull(types.string),
    totalAmount: types.optional(types.number, 0),
    token: types.maybeNull(types.string),
    paymentStatus: types.maybeNull(types.string),
    useStoreCredit: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    isVenmo: false,
    isVenmoPending: false,
  }))
  .actions(self => {
    const setIsVenmo = (value: boolean) => {
      set(self, { isVenmo: value, isVenmoPending: false });
    };
    const setIsVenmoPending = (value: boolean) => {
      set(self, { isVenmoPending: value });
    };
    return {
      setIsVenmo,
      setIsVenmoPending,
    };
  });

export interface ICartPayPalInfoModel
  extends Instance<typeof CartPayPalInfoModel> {}

export const CartAmazonInfoModel = types.model({
  access_token: types.maybeNull(types.string),
  orderReferenceId: types.maybeNull(types.string),
});

export interface ICartAmazonInfoModel
  extends Instance<typeof CartAmazonInfoModel> {}

export const CartCustomerModel = types
  .model({
    id: types.maybeNull(types.number),
    emailAddress: types.maybeNull(types.string),
    phoneNumber: types.maybeNull(types.string),
    storeCredit: types.optional(types.number, 0),
    pageName: types.maybeNull(types.string),
    emailOptIn: types.optional(types.boolean, false),
  })
  .actions(self => {
    const updateId = (customerId: number): void => {
      set(self, { id: customerId });
    };
    return {
      updateId,
    };
  });
export interface ICartCustomerModel
  extends Instance<typeof CartCustomerModel> {}
export const CartCustomFieldDropdownValuesModel = types.model({
  title: types.string,
  value: types.string,
});
export interface ICartCustomFieldDropdownValuesModel
  extends Instance<typeof CartCustomFieldDropdownValuesModel> {}
export const CartCustomFieldModel = types
  .model({
    id: types.number,
    tableName: types.string,
    alias: types.string,
    value: types.string,
    inputType: types.string,
    isRequired: types.string,
    maxLength: types.number,
    question: types.string,
    questionSubText: types.maybeNull(types.string),
    dropdownValues: types.array(CartCustomFieldDropdownValuesModel),
  })
  .views(self => ({
    get isMissingData(): boolean {
      if (self.isRequired.toUpperCase() === 'Y') {
        if (self.value === '') {
          return true;
        }
        if (self.inputType.toUpperCase() === 'CHECKBOX' && self.value === 'N') {
          return true;
        }
      }
      return false;
    },
  }))
  .actions(self => {
    const updateValue = (newValue: string): void => {
      const isBoolean = self.inputType.toUpperCase() === 'CHECKBOX';
      if (isBoolean) {
        const value = newValue === 'true' || newValue === 'Y' ? 'Y' : 'N';
        set(self, { value });
      } else {
        set(self, { value: newValue });
      }
    };
    return {
      updateValue,
    };
  });
export interface ICartCustomFieldModel
  extends Instance<typeof CartCustomFieldModel> {}
export const CartDiscountModel = types.model({
  id: types.string,
  name: types.string,
  value: types.number,
  couponCode: types.string,
  discountType: types.number,
});
export interface ICartDiscountModel
  extends Instance<typeof CartDiscountModel> {}

export const CartGiftCardModel = types.model({
  code: types.string,
  value: types.number,
});
export interface ICartGiftCardModel
  extends Instance<typeof CartGiftCardModel> {}

export type PromoCodeApplyResponse = {
  applied: boolean;
  errorMessage?: string;
};
